import { createContext, useContext, type Component, type JSX } from 'solid-js';
import { Button, type ButtonVariants } from './Button';
import { cn } from '~/lib/utils';

interface ButtonGroupContextValue {
  value: () => string;
  onValueChange: (value: string) => void;
  size: ButtonVariants['size'];
  activeVariant: ButtonVariants['variant'];
  inactiveVariant: ButtonVariants['variant'];
}

const ButtonGroupContext = createContext<ButtonGroupContextValue>();

// ─── Root ──────────────────────────────────────────────────────────────────────

interface ButtonGroupProps {
  value: string;
  onValueChange: (value: string) => void;
  size?: ButtonVariants['size'];
  variant?: ButtonVariants['variant'];
  inactiveVariant?: ButtonVariants['variant'];
  class?: string;
  children: JSX.Element;
}

const ButtonGroupRoot: Component<ButtonGroupProps> & { Item: typeof ButtonGroupItem } = (props) => {
  return (
    <ButtonGroupContext.Provider
      value={{
        value: () => props.value,
        onValueChange: props.onValueChange,
        size: props.size ?? 'sm',
        activeVariant: props.variant ?? 'primary',
        inactiveVariant: props.inactiveVariant ?? 'outlinePrimary',
      }}
    >
      <div role="group" class={cn('flex', props.class)}>
        {props.children}
      </div>
    </ButtonGroupContext.Provider>
  );
};

// ─── Item ──────────────────────────────────────────────────────────────────────

interface ButtonGroupItemProps {
  value: string;
  disabled?: boolean;
  class?: string;
  children: JSX.Element;
}

const ButtonGroupItem: Component<ButtonGroupItemProps> = (props) => {
  const ctx = useContext(ButtonGroupContext)!;
  const isSelected = () => ctx.value() === props.value;

  return (
    <Button
      aria-pressed={isSelected()}
      variant={isSelected() ? ctx.activeVariant : ctx.inactiveVariant}
      size={ctx.size}
      disabled={props.disabled}
      onClick={() => ctx.onValueChange(props.value)}
      class={cn(
        'flex-1',
        '[&:not(:last-child)]:rounded-e-none [&:not(:first-child)]:rounded-s-none',
        '[&:not(:last-child)]:-mr-px',
        props.class
      )}
    >
      {props.children}
    </Button>
  );
};

ButtonGroupRoot.Item = ButtonGroupItem;

export const ButtonGroup = ButtonGroupRoot;
export { ButtonGroupItem };
